import type { ReactNode } from 'react';
import { Icon, type IconName } from './icon/index.ts';
import styles from './Button.module.css';

/**
 * Design system § 14 — Button.
 *
 * `disabled` and `disabledReason` are one thing, not two: § 12 requires a
 * disabled control to make its reason legible rather than merely dimming, so
 * the type makes the reason impossible to omit. A control gated by a timer
 * says it is waiting.
 *
 * There is deliberately no `className` and no `style`. The button owns its
 * appearance; the parent owns its placement.
 */

export type ButtonVariant =
  | 'primary'
  | 'primary-gradient'
  | 'secondary'
  | 'ghost'
  | 'destructive';

interface ButtonBaseProps {
  children: ReactNode;
  variant?: ButtonVariant;
  type?: 'button' | 'submit';
  onClick?: () => void;
  /** Drawn before the label, decorative — the label already carries meaning. */
  icon?: IconName;
  /** Set when the label alone does not name the action (§ 12). */
  ariaLabel?: string;
  ariaKeyShortcuts?: string;
  id?: string;
  /**
   * § 16 — `compact` is the administrative height, for a button that is one of
   * a dozen on a toolbar or inside a table row rather than the action a screen
   * is about. It steps to § 12's minimum target at touch, so density never
   * costs a target.
   */
  size?: 'default' | 'compact';
  /** Marks the button as the one currently in effect — a page in Pagination. */
  ariaCurrent?: 'page' | 'true';
  /** Set on a sortable table header, where the button IS the sort control. */
  ariaPressed?: boolean;
}

interface EnabledButtonProps extends ButtonBaseProps {
  disabled?: false;
  disabledReason?: never;
}

interface DisabledButtonProps extends ButtonBaseProps {
  disabled: true;
  /**
   * Why the control cannot be used, in the learner's own words. Rendered as
   * the accessible description, so the reason is available to everyone and not
   * only to a reader who can see a dimmed surface (§ 12).
   */
  disabledReason: string;
}

export type ButtonProps = EnabledButtonProps | DisabledButtonProps;

export function Button({
  children,
  variant = 'primary',
  type = 'button',
  onClick,
  icon,
  ariaLabel,
  ariaKeyShortcuts,
  ariaCurrent,
  ariaPressed,
  id,
  size = 'default',
  disabled = false,
  disabledReason,
}: ButtonProps) {
  return (
    <button
      id={id}
      type={type}
      className={`${styles.button} type-button`}
      data-variant={variant}
      data-size={size}
      data-state={disabled ? 'disabled' : 'default'}
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      aria-keyshortcuts={ariaKeyShortcuts}
      aria-current={ariaCurrent}
      aria-pressed={ariaPressed}
      aria-describedby={disabled ? `${id ?? 'button'}-reason` : undefined}
      title={disabled ? disabledReason : undefined}
    >
      {icon ? <Icon name={icon} size={size === 'compact' ? 16 : 20} /> : null}
      {children}
      {disabled ? (
        <span id={`${id ?? 'button'}-reason`} hidden>
          {disabledReason}
        </span>
      ) : null}
    </button>
  );
}
